import i18n from "i18next";
import type { TFunction } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";
import { initReactI18next } from "react-i18next";
import { rebranderTraductions } from "@dozka/ui";

export const SUPPORTED_LOCALES = ["en", "fr", "es", "it"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

// Chargement des locales À LA DEMANDE (lazy) plutôt que d'embarquer en+es+fr
// (~440 Ko brut) dans le chunk d'entrée, parsés au boot alors qu'UNE seule
// langue sert. `resourcesToBackend` + `import()` dynamique fait émettre à Vite
// un chunk JSON SÉPARÉ par langue (`locales/en.json`, `locales/fr.json`,
// `locales/es.json`) ; seul `lng` détecté (+ le fallback `en`) est fetché au
// boot, les autres se chargent au changement de langue.
//
// La promesse est awaitée dans `main.tsx` AVANT le 1er render : la langue
// détectée est donc déjà chargée au premier paint → pas de flash de clés ni de
// Suspense (on garde `useSuspense: false` pour que le changement de langue à
// chaud ne suspende pas — les composants re-render quand le nouveau bundle
// arrive).
let demarrage: Promise<TFunction> | null = null;

/**
 * Monte i18next. **Rien ne se monte à l'import de ce module — il faut appeler
 * cette fonction**, et c'est tout l'intérêt.
 *
 * 🩸 Jusqu'au 18-08-2026, `.init()` partait à l'ÉVALUATION du module : importer
 * ce fichier, même pour la seule constante `SUPPORTED_LOCALES`, montait
 * i18next. i18next est un SINGLETON de processus et la batterie tourne dans un
 * seul fork (`pool: "forks"` + `singleFork: true` dans `vitest.config.ts`) :
 * un fichier de test qui touchait ce module, fût-ce par un import transitif,
 * faisait basculer TOUS les fichiers suivants de « `t()` rend la clé » à
 * « `t()` rend l'anglais ». C'est ce qui a rendu rouges les neuf cas de
 * `CyranoPanel.rail.render.test.tsx` — verts quand on lançait le fichier seul,
 * rouges dans la batterie, sans qu'aucune assertion ait changé (cf. #479).
 *
 * ⚠️ Ce que ça ne répare PAS : appeler `initI18n()` dans un test monte le
 * singleton pour tout le reste du fork, exactement comme avant. La voie
 * ACCIDENTELLE (un import transitif) est fermée ; la voie DÉLIBÉRÉE ne l'est
 * pas et ne peut pas l'être ici — un test qui veut l'i18n pour de vrai doit
 * savoir qu'il la laisse montée derrière lui. Les tests qui veulent la CLÉ
 * épinglent `t()` (`vi.mock("react-i18next", …)`, idiome de ~19 fichiers)
 * plutôt que de compter sur l'état ambiant.
 *
 * Idempotente : les entrées de dev montent la même app que `main.tsx` et
 * peuvent l'appeler à leur tour ; le second appel rend la première promesse
 * sans relancer d'init (i18next lèverait sinon un avertissement et rejouerait
 * la détection).
 */
export function initI18n(): Promise<TFunction> {
  if (!demarrage) {
    demarrage = i18n
      .use(LanguageDetector)
      .use(
        resourcesToBackend((language: string) =>
          import(`./locales/${language}.json`).then((m) =>
            // Sur `cyrano.work`, aucune traduction ne dit « Dozka » (cf `core/marque`).
            rebranderTraductions(m.default ?? m),
          ),
        ),
      )
      .use(initReactI18next)
      .init({
        fallbackLng: "en",
        supportedLngs: SUPPORTED_LOCALES,
        interpolation: { escapeValue: false },
        react: { useSuspense: false },
        detection: {
          order: ["localStorage", "navigator"],
          lookupLocalStorage: "cockpit.locale",
          caches: ["localStorage"],
        },
      });
  }
  return demarrage;
}

export default i18n;
